import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  MONTHS_SHORT,
  WEEKDAYS,
  buildMonthCells,
  isYmdInRange,
  localTodayYmd,
  monthIntersectsRange,
  parseYmd,
  shiftMonth,
  toYmd,
  yearBounds,
} from "./dateParts";

const navButtonClass =
  "inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-indigo-50 hover:text-indigo-700 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent";

/**
 * Calendar with a year list, so Safari can jump to a year the way Chrome does.
 * `value`, `min`, and `max` are YYYY-MM-DD.
 */
const YearJumpCalendar = ({ value, min, max, onSelect, today }) => {
  const selected = parseYmd(value);
  const todayYmd = today || localTodayYmd();
  const initial = selected || parseYmd(todayYmd) || new Date();
  const [cursor, setCursor] = useState({
    year: initial.getFullYear(),
    month: initial.getMonth(),
  });
  const [view, setView] = useState("days");
  const [jumpYear, setJumpYear] = useState(initial.getFullYear());
  const yearListRef = useRef(null);

  useEffect(() => {
    if (!selected) return;
    setCursor({ year: selected.getFullYear(), month: selected.getMonth() });
    setJumpYear(selected.getFullYear());
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    if (view !== "jump" || !yearListRef.current) return;
    const list = yearListRef.current;
    const selectedYear = list.querySelector("[data-selected='true']");
    if (!selectedYear) return;
    const top =
      selectedYear.getBoundingClientRect().top -
      list.getBoundingClientRect().top +
      list.scrollTop;
    list.scrollTop = top - list.clientHeight / 2 + selectedYear.clientHeight / 2;
  }, [view, jumpYear]);

  const bounds = yearBounds(min, max);
  const years = [];
  for (let year = bounds.minYear; year <= bounds.maxYear; year += 1) {
    years.push(year);
  }

  const openJump = () => {
    setJumpYear(cursor.year);
    setView("jump");
  };

  const goMonth = (delta) => {
    const next = shiftMonth(cursor.year, cursor.month, delta);
    if (!monthIntersectsRange(next.year, next.month, min, max)) return;
    setCursor(next);
  };

  const chooseMonth = (monthIndex) => {
    if (!monthIntersectsRange(jumpYear, monthIndex, min, max)) return;
    setCursor({ year: jumpYear, month: monthIndex });
    setView("days");
  };

  const chooseDay = (date) => {
    const ymd = toYmd(date);
    if (!isYmdInRange(ymd, min, max)) return;
    onSelect(ymd);
  };

  const prevDisabled = !monthIntersectsRange(
    shiftMonth(cursor.year, cursor.month, -1).year,
    shiftMonth(cursor.year, cursor.month, -1).month,
    min,
    max,
  );
  const nextDisabled = !monthIntersectsRange(
    shiftMonth(cursor.year, cursor.month, 1).year,
    shiftMonth(cursor.year, cursor.month, 1).month,
    min,
    max,
  );

  return (
    <div className="w-[280px] select-none rounded-lg border border-slate-200 bg-white p-3 text-slate-800 shadow-lg">
      {view === "days" ? (
        <>
          <div className="mb-2 flex items-center justify-between gap-2">
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-sm font-semibold text-slate-800 hover:bg-indigo-50 hover:text-indigo-800"
              onClick={openJump}
              aria-label="Choose year and month"
            >
              {MONTHS_SHORT[cursor.month]} {cursor.year}
              <svg className="h-3.5 w-3.5 text-slate-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
              </svg>
            </button>
            <div className="flex items-center gap-0.5">
              <button type="button" className={navButtonClass} onClick={() => goMonth(-1)} disabled={prevDisabled} aria-label="Previous month">
                <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                </svg>
              </button>
              <button type="button" className={navButtonClass} onClick={() => goMonth(1)} disabled={nextDisabled} aria-label="Next month">
                <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-0.5 text-center text-[11px] font-medium text-slate-400">
            {WEEKDAYS.map((day) => (
              <div key={day} className="py-1">
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-0.5">
            {buildMonthCells(cursor.year, cursor.month).map(({ date, outside }) => {
              const ymd = toYmd(date);
              const disabled = !isYmdInRange(ymd, min, max);
              const isSelected = value === ymd;
              const isToday = ymd === todayYmd;
              return (
                <button
                  key={ymd}
                  type="button"
                  disabled={disabled}
                  onClick={() => chooseDay(date)}
                  className={`h-8 rounded-md text-sm ${
                    isSelected
                      ? "bg-indigo-600 font-semibold text-white"
                      : disabled
                        ? "cursor-not-allowed text-slate-300"
                        : outside
                          ? "text-slate-300 hover:bg-indigo-50 hover:text-indigo-700"
                          : "text-slate-700 hover:bg-indigo-50 hover:text-indigo-800"
                  } ${isToday && !isSelected ? "ring-1 ring-inset ring-indigo-300" : ""}`}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>
        </>
      ) : (
        <>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-800">
              {MONTHS_SHORT[cursor.month]} {jumpYear}
            </p>
            <button type="button" className="rounded-md px-2 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-50" onClick={() => setView("days")}>
              Back
            </button>
          </div>

          <div
            ref={yearListRef}
            className="mb-2 max-h-[148px] overflow-y-auto rounded-md border border-slate-200"
            role="listbox"
            aria-label="Year"
          >
            {years.map((year) => {
              const selectedYear = year === jumpYear;
              return (
                <button
                  key={year}
                  type="button"
                  role="option"
                  aria-selected={selectedYear}
                  data-selected={selectedYear ? "true" : "false"}
                  onClick={() => setJumpYear(year)}
                  className={`block w-full border-b border-slate-100 px-3 py-1.5 text-left text-sm last:border-b-0 ${
                    selectedYear
                      ? "bg-indigo-600 font-semibold text-white"
                      : "text-slate-800 hover:bg-slate-100"
                  }`}
                >
                  {year}
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-4 gap-1.5">
            {MONTHS_SHORT.map((label, monthIndex) => {
              const disabled = !monthIntersectsRange(jumpYear, monthIndex, min, max);
              const selectedMonth = jumpYear === cursor.year && monthIndex === cursor.month;
              return (
                <button
                  key={label}
                  type="button"
                  disabled={disabled}
                  onClick={() => chooseMonth(monthIndex)}
                  className={`rounded-md px-1 py-1.5 text-sm ${
                    selectedMonth
                      ? "bg-indigo-600 font-semibold text-white"
                      : disabled
                        ? "cursor-not-allowed text-slate-300"
                        : "text-slate-700 hover:bg-indigo-50"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default YearJumpCalendar;
